import {
  GROUP_ROW_GRAND_TOTAL,
  GROUP_ROW_GROUP,
  GROUP_ROW_SUB_TOTAL,
  PERCENTAGE_FROM_FRACTION_MULTIPLIER,
  PERCENTAGE_FROM_FRACTION_SUB_TYPE,
  SUMMARY_ROW,
} from "../types/constants";

export function buildMetricItem(columnName, colDef, methodOverride) {
  const colType = colDef?._type;
  const colSubType = colDef?._subType;
  const isNumeric = colType === "INT" || colType === "FLOAT";
  const isDate = colType === "DATE";
  const method =
    methodOverride ?? (isNumeric ? "SUM" : isDate ? "MAX" : "COUNT_UNIQUE");
  const baseName = colDef?.displayName ?? columnName;
  const label = methodOverride ? `${baseName} (${methodOverride})` : baseName;
  const metric = { method, key: columnName, label };
  if (colSubType === "CURRENCY") metric.prefix = "$";
  if (colSubType === "PERCENTAGE" || colSubType === "TARGET_PERCENTAGE")
    metric.suffix = "%";
  if (colSubType === PERCENTAGE_FROM_FRACTION_SUB_TYPE) {
    metric.suffix = "%";
    metric.multiplier = PERCENTAGE_FROM_FRACTION_MULTIPLIER;
  }
  if (isDate) metric.dataType = "date";
  if (colType === "ENUM") {
    metric.chartType = "enumButtons";
    metric.options = colDef._options;
    if (colSubType === "ENUM_STATUS_BUTTONS") metric.statusChart = true;
  }
  return metric;
}

const EXCLUDED_METRIC_ROW_TYPES = new Set([
  SUMMARY_ROW,
  GROUP_ROW_GRAND_TOTAL,
  GROUP_ROW_SUB_TOTAL,
]);

/** Returns the rows metrics are calculated over: group rows if any exist, otherwise all rows except summary and total rows. */
export function selectMetricRows(rows, controlColumn) {
  if (!Array.isArray(rows)) return rows;
  if (!controlColumn) return rows;
  const candidates = rows.filter(
    (row) => !EXCLUDED_METRIC_ROW_TYPES.has(row?.[controlColumn])
  );
  const groupRows = candidates.filter(
    (row) => row?.[controlColumn] === GROUP_ROW_GROUP
  );
  return groupRows.length > 0 ? groupRows : candidates;
}

/** Returns the set of column keys referenced by a formula tree. */
export function collectFormulaKeys(node, keys = new Set()) {
  if (!node || typeof node !== "object") return keys;
  if (node.key) keys.add(node.key);
  collectFormulaKeys(node.a, keys);
  collectFormulaKeys(node.b, keys);
  return keys;
}

/** Returns the column keys referenced by the given metric items. */
export function collectMetricKeys(metricsItems) {
  const keys = new Set();
  for (const item of metricsItems ?? []) {
    if (item?.formula) collectFormulaKeys(item.formula, keys);
    else if (item?.key) keys.add(item.key);
  }
  return keys;
}

const isFiniteNumber = (value) =>
  typeof value === "number" && Number.isFinite(value);

/** Returns rows with non-numeric values removed from any of the keys that hold numeric data. */
export function stripNonNumericValues(rows, keys) {
  if (!Array.isArray(rows)) return rows;
  const numericKeys = [...(keys ?? [])].filter((key) =>
    rows.some((row) => isFiniteNumber(row?.[key]))
  );
  if (numericKeys.length === 0) return rows;
  return rows.map((row) => {
    if (numericKeys.every((key) => isFiniteNumber(row?.[key]))) return row;
    const cleaned = { ...row };
    for (const key of numericKeys) {
      if (!isFiniteNumber(cleaned[key])) delete cleaned[key];
    }
    return cleaned;
  });
}

/** Returns the value multiplied by the multiplier when both are usable, otherwise the value unchanged. */
export function applyMetricMultiplier(value, multiplier) {
  if (!isFiniteNumber(value) || multiplier === undefined || multiplier === null)
    return value;
  return value * multiplier;
}
